import React, {Fragment} from "react";
import {
    Accordion, AccordionDetails, AccordionSummary,
    Button,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Typography
} from "@mui/material";
import {sprintf} from "sprintf-js";
import {ChecklistField, LeadChecklistField} from "../formFields/ChecklistField";
import {Select} from "formik-mui";
import {ErrorMessage, Field, FieldArray, getIn, useFormikContext} from "formik";
import {FullWidthText} from "../formFields";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import * as Yup from "yup";
import ChecklistItemDefault from "../defaults/ChecklistItemDefault";


const [section, name, title] = [4, "OtherRooms", "Other Rooms Used for Living & Halls"];

const emptyRoomObject = () => {
    return {
        "1": {
            code: "",
            floorLevel: "",
            location: {
                x: "",
                y: ""
            },
        },
        "2": ChecklistItemDefault,
        "3": ChecklistItemDefault,
        "4": ChecklistItemDefault,
        "5": ChecklistItemDefault,
        "6": ChecklistItemDefault,
        "7": ChecklistItemDefault,
        "8": ChecklistItemDefault,
        "9": ChecklistItemDefault,
        "10": ChecklistItemDefault,
    };
}

const OtherRoomsDefaults = () => {
    return {"4": []};
}

const RegularItems = (props) => {
    const {index} = {...props};
    return (<>
        <ChecklistField
            path={sprintf("%s.%s.%s", name, index, "electricity")}
            rank={sprintf("4.%s.2", index)}
            room={name}
            desc={"Electricity / Illumination"}
            section={section}
        />
        <ChecklistField
            path={sprintf("%s.%s.%s", name, index, "electricalHazard")}
            rank={sprintf("4.%s.3", index)}
            room={name}
            desc={"Free of Electrical Hazards"}
            section={section}
        />
        <ChecklistField
            path={sprintf("%s.%s.%s", name, index, "security")}
            rank={sprintf("4.%s.4", index)}
            room={name}
            desc={"Security"}
            section={section}
        />
        <ChecklistField
            path={sprintf("%s.%s.%s", name, index, "windowCondition")}
            rank={sprintf("4.%s.5", index)}
            room={name}
            desc={"Window Condition"}
            section={section}
        />
        <ChecklistField
            path={sprintf("%s.%s.%s", name, index, "ceilingCondition")}
            rank={sprintf("4.%s.6", index)}
            room={name}
            desc={"Ceiling Condition"}
            section={section}
        />
        <ChecklistField
            path={sprintf("%s.%s.%s", name, index, "wallCondition")}
            rank={sprintf("4.%s.7", index)}
            room={name}
            desc={"Wall Condition"}
            section={section}
        />
        <ChecklistField
            path={sprintf("%s.%s.%s", name, index, "floorCondition")}
            rank={sprintf("4.%s.8", index)}
            room={name}
            desc={"Floor Condition"}
            section={section}
        />
        <LeadChecklistField
            path={sprintf("%s.%s.%s", name, index, "leadPaint")}
            rank={sprintf("4.%s.9", index)}
            room={name}
            desc={"Lead-Based Paint"}
            section={section}
        />
        <ChecklistField
            path={sprintf("%s.%s.%s", name, index, "smokeDetectors")}
            rank={sprintf("4.%s.10", index)}
            room={name}
            desc={"Smoke Detectors"}
            section={section}
        />
    </>);
}

const RoomCodeAndLocation = (props) => {
    const {index} = props;
    const {touched, errors} = useFormikContext();
    const [codeName, locationX, locationY] = [sprintf("4.%s.1.code", index),
        sprintf("4.%s.1.location.x", index), sprintf("4.%s.1.location.y", index)];

    return <>
        <Grid item xs={12} sm={12}>
            <Typography variant={"subtitle2"} gutterBottom>
                {"4.1 Room Code & Room Location"}
            </Typography>
        </Grid>
        <Grid item xs={12} sm={12}>
            <FormControl
                fullWidth
                error={getIn(touched, codeName) && !!getIn(errors, codeName)}
            >
                <InputLabel htmlFor={codeName}>Room Code</InputLabel>
                <Field
                    component={Select}
                    name={codeName}
                    inputProps={{id: codeName}}
                    defaultValue={""}
                    validate={(value) => {
                        if (!!value) {
                            const result = parseFloat(value);
                            if (!!result) {
                                if (result < 7 && result > 0) {
                                    return "";
                                }
                                return "Room Code should be a value between 1 and 6"
                            }
                        }
                        return "Required";
                    }}
                >
                    <MenuItem value={"1"}>1 - A Bedroom or any other room used for sleeping (regardless of room
                        type)</MenuItem>
                    <MenuItem value={"2"}>2 - Dining Room or Dining Area</MenuItem>
                    <MenuItem value={"3"}>3 - Second Living Room, Family Room, Den, Playroom, or TV Room</MenuItem>
                    <MenuItem value={"4"}>4 - Entrance Halls, Corridors, Halls, or Staircases</MenuItem>
                    <MenuItem value={"5"}>5 - Additional Bathroom</MenuItem>
                    <MenuItem value={"6"}>6 - Other (e.g. Basement)</MenuItem>
                </Field>
                <ErrorMessage name={codeName}
                              className={"MuiFormHelperText-root Mui-error"} component={"div"}/>
            </FormControl>

        </Grid>
        <Grid item xs={12} sm={12}>
            <FullWidthText
                name={`4.${index}.1.floorLevel`}
                label={"Floor Level"}
                validate={async (value) => {
                    const [ MIN, MAX ] = [-99, 99];
                    const msg = sprintf("Floor level should be a whole number between %s and %s", MIN, MAX);
                    try {
                        await Yup.number().required("Required").integer(msg).min(MIN, msg).max(MAX, msg)
                            .validateSync(value);
                    } catch (error) {
                        if (error.errors[0].includes("NaN")) {
                            return msg;
                        }
                        return error.errors[0];
                    }
                    return "";
                }}
            />
        </Grid>
        <Grid item xs={12} sm={12}>
            <Typography variant={"subtitle2"} gutterBottom>
                {"Relative Location"}
            </Typography>
        </Grid>
        <Grid item xs={12} sm={12}>
            <FormControl
                fullWidth
                error={getIn(touched, locationX) && !!getIn(errors, locationX)}
            >
                <InputLabel htmlFor={locationX}>Left / Center / Right</InputLabel>
                <Field
                    component={Select}
                    name={locationX}
                    inputProps={{id: locationX}}
                    defaultValue={""}
                    validate={(value) => {
                        if (!!value && value.match(/left|right|center/)) {
                            return "";
                        }
                        return "Required";
                    }}
                >
                    <MenuItem value={"left"}>Left</MenuItem>
                    <MenuItem value={"center"}>Center</MenuItem>
                    <MenuItem value={"right"}>Right</MenuItem>
                </Field>
                <ErrorMessage name={locationX}
                              className={"MuiFormHelperText-root Mui-error"} component={"div"}/>
            </FormControl>

        </Grid>
        <Grid item xs={12} sm={12}>
            <FormControl
                error={getIn(touched, locationY) && !!getIn(errors, locationY)}
                fullWidth
            >
                <InputLabel htmlFor={locationY}>Front / Center / Rear</InputLabel>
                <Field
                    component={Select}
                    name={locationY}
                    inputProps={{id: locationY}}
                    defaultValue={""}
                    validate={(value) => {
                        if (!!value && value.match(/front|center|rear/)) {
                            return "";
                        }
                        return "Required";
                    }}
                >
                    <MenuItem value={"front"}>Front</MenuItem>
                    <MenuItem value={"center"}>Center</MenuItem>
                    <MenuItem value={"rear"}>Rear</MenuItem>
                </Field>
                <ErrorMessage name={locationY}
                              className={"MuiFormHelperText-root Mui-error"} component={"div"}/>
            </FormControl>
        </Grid>
    </>;
};

const OtherRooms = (props) => {
    const {values} = useFormikContext();
    return <>
        <Typography variant={"h6"} gutterBottom>
            {sprintf("%d. %s", section, title)}
        </Typography>
        <FieldArray
            name={"4"}
            render={(arrayHelpers) => (<>
                {values["4"] && values["4"].length > 0 ? (
                    values["4"].map((room, index) => (
                        <Fragment key={index}>
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon/>} key={index}>
                                    <Typography variant={"h6"} gutterBottom>
                                        Room {index + 1}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={3}>
                                        <RoomCodeAndLocation index={index}/>
                                        <RegularItems index={index}/>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                            <Grid
                                container
                                direction={"row"}
                                justifyContent={"center"}
                                alignItems={"center"}
                                spacing={1}
                            >
                                <Grid item>
                                    <Button
                                        variant={"contained"}
                                        color={"primary"}
                                        size={"small"}
                                        type="button"
                                        onClick={() => arrayHelpers.remove(index)} // remove a room from the list
                                    >
                                        Remove room
                                    </Button>
                                </Grid>
                                {values["4"].length < 6 ? (
                                    <Grid item>
                                        <Button
                                            variant={"contained"}
                                            color={"primary"}
                                            size={"small"}
                                            type="button"
                                            onClick={() => arrayHelpers.push(emptyRoomObject())}// insert an empty string at a position
                                        >
                                            Add room
                                        </Button>
                                    </Grid>
                                ) : null}
                            </Grid>
                        </Fragment>))
                ) : (
                    <Grid item>
                        <Button
                            variant={"contained"}
                            color={"primary"}
                            size={"small"}
                            type="button"
                            onClick={() => arrayHelpers.push(emptyRoomObject())}>
                            {/* show this when user has removed all friends from the list */}
                            Add room
                        </Button>
                    </Grid>
                )}
            </>)}
        />
    </>;
};

export default OtherRooms;
export {OtherRoomsDefaults};